import React, { useEffect, useRef } from 'react';
import {
  FileLike,
  UploadWidgetColors,
  UploadWidgetFailedFile,
  UploadWidgetOnUpdateEvent,
  UploadWidgetResult,
} from '@bytescale/upload-widget';
import {
  UploadDropzone,
  UploadDropzoneConfig,
} from '@bytescale/upload-widget-react';

import { compressImageFile } from 'app/shared/utils/useMultiImageUpload';
import {
  AspectRatio,
  ImageForDropzone,
  MultiImageUploaderMimeType,
} from 'app/shared/typings';

// Bytescale's own upload has no timeout, so we warn the user ourselves past this duration.
// Only counts time spent actually uploading (not time spent awaiting crop/edit input).
const SLOW_UPLOAD_THRESHOLD_MS = 20000;
const SLOW_UPLOAD_CHECK_INTERVAL_MS = 5000;

export interface MultiImageDropzoneProps {
  objectType: string;
  objectId?: number;
  ratio: AspectRatio;
  multipleImagesPerRatio?: boolean;
  imagesForRatio: ImageForDropzone[];
  imagesForAllRatios: ImageForDropzone[];
  setImagesForAllRatios: (values: ImageForDropzone[]) => void;
  setFailedFiles?: (values: UploadWidgetFailedFile[]) => void;
  setIsUploadSlow?: (isSlow: boolean) => void;
  maxFileSizeMegabytes: number;
  mimeTypes?: MultiImageUploaderMimeType[];
  colors?: UploadWidgetColors;
  fontFamily: string;
  width?: string;
  height?: string;
}

const megabytesToBytes = (megabytes: number) => megabytes * 1024 * 1024;

// Returns a decimal number e.g. 1.6666666666666
const getCropperRatio = (ratio: AspectRatio) => {
  const cropperRatioParts = ratio
    .split(':')
    .map((ratioPart: string) => Number(ratioPart));
  return cropperRatioParts[0] / cropperRatioParts[1];
};

// Type definition: https://github.com/upload-io/uploader#get-the-result
const getImageInfoFromRawImage = (
  rawImage: UploadWidgetResult,
  ratio: AspectRatio
) => ({
  ratio,
  filePath: rawImage.filePath,
  fileUrl: rawImage.fileUrl,
  editedFileUrl: rawImage.editedFile?.fileUrl,
  originalFileUrl: rawImage.originalFile.fileUrl,
  originalFilename: rawImage.originalFile.originalFileName,
  mime: rawImage.originalFile.mime,
  size: rawImage.originalFile.size,
});

const getImagesWithAddedImages = (
  currentImages: ImageForDropzone[],
  rawImagesForRatio: UploadWidgetResult[],
  ratio: AspectRatio
) => {
  const newImage = rawImagesForRatio.find(
    (rawImage: UploadWidgetResult) =>
      !currentImages.find(
        (image: ImageForDropzone) =>
          image.filePath === rawImage.filePath && image.ratio === ratio
      )
  );

  if (newImage === undefined) {
    return currentImages;
  }

  const updatedImages = currentImages.concat(
    getImageInfoFromRawImage(newImage, ratio)
  );
  return updatedImages;
};

const getImagesWithRemovedImages = (
  currentImages: ImageForDropzone[],
  rawImagesForRatio: UploadWidgetResult[],
  ratio: AspectRatio
) => {
  const updatedFilePathsForRatio = rawImagesForRatio.map(
    (rawImage: UploadWidgetResult) => rawImage.filePath
  );
  // Keep only images where EITHER:
  // - ratio is different from the ratio we're curently working with (which means image is a saved image from another ratio)
  // - ratio is the same and file path is one of the updated set of file paths for this ratio
  const updatedImages = currentImages.filter(
    (currentImage: ImageForDropzone) =>
      currentImage.ratio != ratio ||
      updatedFilePathsForRatio.includes(currentImage.filePath)
  );
  return updatedImages;
};

export const MultiImageDropzone = ({
  objectType,
  objectId,
  ratio,
  multipleImagesPerRatio,
  imagesForRatio,
  imagesForAllRatios,
  setImagesForAllRatios,
  setFailedFiles,
  setIsUploadSlow,
  maxFileSizeMegabytes,
  mimeTypes,
  colors,
  fontFamily,
  width = '400px',
  height = '275px',
}: MultiImageDropzoneProps) => {
  const cropRatio = getCropperRatio(ratio);
  const ratioForFileName = String(cropRatio).substring(0, 7);
  const objectIdForFilename = objectId || '0';

  // How long each currently-pending file has been uploading, for the slow-upload warning.
  const pendingSinceRef = useRef<Map<FileLike, number>>(new Map());

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const hasSlowUpload = Array.from(pendingSinceRef.current.values()).some(
        (pendingSince) => now - pendingSince > SLOW_UPLOAD_THRESHOLD_MS
      );
      setIsUploadSlow && setIsUploadSlow(hasSlowUpload);
    }, SLOW_UPLOAD_CHECK_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
  }, [setIsUploadSlow]);

  const options: UploadDropzoneConfig = {
    apiKey: process.env.UPLOAD_IO_KEY || 'free',
    editor: {
      images: {
        crop: true,
        cropRatio,
        preview: true,
      },
    },
    maxFileCount: 10,
    maxFileSizeBytes: megabytesToBytes(maxFileSizeMegabytes),
    metadata: {
      objectType,
      objectId,
    },
    mimeTypes,
    multi: multipleImagesPerRatio,
    onPreUpload: async (file) => ({
      transformedFile: await compressImageFile(file),
    }),
    path: {
      fileName: `${objectType}-${objectIdForFilename}-${ratioForFileName}-{UTC_DATE}-{UTC_TIME}-{UNIQUE_DIGITS_10}-image{ORIGINAL_FILE_EXT}`,
      fileNameFallback: `${objectType}-${objectIdForFilename}-${ratioForFileName}-{UTC_DATE}-{UTC_TIME}-{UNIQUE_DIGITS_10}-image{ORIGINAL_FILE_EXT}`,
      fileNameVariablesEnabled: true,
      folderPath: process.env.UPLOAD_IO_FOLDER,
    },
    showFinishButton: false, // If this is false, you use onUpdate. If true, you use onComplete.
    showRemoveButton: true,
    styles: {
      colors,
      fontFamilies: {
        base: fontFamily,
      },
      fontSizes: {
        base: 16,
      },
    },
  };

  const onUpdate = ({
    failedFiles,
    pendingFiles,
    uploadedFiles,
  }: UploadWidgetOnUpdateEvent) => {
    const now = Date.now();
    // "AwaitingUserInput" means the file already finished uploading and is just waiting on
    // the user to crop/confirm it - that's not a network problem, so it's excluded here.
    const stillUploadingFiles = new Set(
      pendingFiles
        .filter(({ status }) => status !== 'AwaitingUserInput')
        .map(({ file }) => file)
    );

    pendingSinceRef.current.forEach((_pendingSince, file) => {
      if (!stillUploadingFiles.has(file)) {
        pendingSinceRef.current.delete(file);
      }
    });
    stillUploadingFiles.forEach((file) => {
      if (!pendingSinceRef.current.has(file)) {
        pendingSinceRef.current.set(file, now);
      }
    });
    if (stillUploadingFiles.size === 0) {
      setIsUploadSlow && setIsUploadSlow(false);
    }

    setFailedFiles && setFailedFiles(failedFiles);

    if (uploadedFiles && uploadedFiles.length > imagesForRatio.length) {
      // User ADDED an image for this ratio
      setImagesForAllRatios(
        getImagesWithAddedImages(imagesForAllRatios, uploadedFiles, ratio)
      );
    } else if (!uploadedFiles || uploadedFiles.length < imagesForRatio.length) {
      // User REMOVED an image from this ratio
      setImagesForAllRatios(
        getImagesWithRemovedImages(imagesForAllRatios, uploadedFiles, ratio)
      );
    }
  };

  return (
    <UploadDropzone
      options={options}
      onUpdate={onUpdate}
      width={width}
      height={height}
    />
  );
};
